// 7. 10. 2026 (owner, Ammo and Fuel): which weapons burn Ammo or Fuel, and the rules of Ammo per shot. Only the item
// catalog is imported, so the stat block (formulas.ts) and the loot (expedition.ts) can use it without a cycle.

import { itemTemplate } from '../content/items';
import type { ZoneId } from '../types';

/** Ranged weapons that do not shoot bullets. 11. 10. 2026 (BACKLOG #36): the bows, crossbows, knives and the dart pistol of the item package. */
export const QUIET_RANGED = ['slingshot', 'recurve_bow', 'stalker_crossbow', 'compound_bow', 'foundry_arbalest', 'throwing_knives', 'hand_crossbow', 'halcyon_dart_pistol'] as const;

/** What a weapon burns: firearms Ammo (one a shot), the Chainsaw Fuel (one a fight), the rest nothing. */
export function weaponFeed(templateId: string | null | undefined): 'ammo' | 'fuel' | null {
  if (!templateId) return null;
  if (templateId === 'chainsaw') return 'fuel';
  const t = itemTemplate(templateId);
  if (t.slot !== 'weapon') return null;
  const ranged = t.weaponClass === 'ranged_1h' || t.weaponClass === 'ranged_2h';
  return ranged && !(QUIET_RANGED as readonly string[]).includes(t.id) ? 'ammo' : null;
}

/**
 * Ammo per shot (owner, 7. 10. 2026; before: one round a fight). Every attack of a firearm fires one round (the
 * opening shot of round 0, every round, Kickback's extra attack) and Burst's extra hit one more; bows, crossbows and
 * the slingshot fire none. When the rounds run out in a fight, the rest of it deals −30% (`DRY_DAMAGE_MULT`).
 */
export const AMMO_RULES = {
  /** One round a shot; false: one a fight, the rule before 7. 10. 2026 (simulations only). */
  perShot: true,
  /**
   * Shots a firearm fires in an NPC fight of a zone: `pnpm --filter @lastday/shared exec tsx scripts/shots-sim.ts`
   * (Rare gear, the zone's levels, new player orders), the average of the Pistolero and the Gunner, to a half.
   */
  shotsPerFight: {
    forest: 5.5,
    gas_station: 6,
    downtown: 5.5,
    hospital: 4.5,
    factory: 4.5,
    military_base: 4,
    mountains: 4.5,
    wasteland: 5,
    secret_bunker: 4.5,
    underground_lab: 5,
    survivor_vault: 4.5,
  } as Record<ZoneId, number>,
  /**
   * The order sheet's reserve: this share of the expected shots on top, plus `spareFights` fights' worth. 7. 10. 2026
   * (owner, round 2): 0.25 and 2 left the relaxed player without rounds in 7% of fights (long runs meet more fights than
   * expected); the reserve grows instead of the finds, for at most 5% in every profile. 9. 10. 2026 (owner): the fights
   * are the run's `fightsQ` quantile (the 90th percentile, not the average), so the share on top is 0.
   */
  spareShare: 0,
  spareFights: 3,
  /** 9. 10. 2026 (owner): the order sheet counts the fights a run meets at most in this share of runs (`fightsQuantile`). */
  fightsQ: 0.9,
  /** 7. 10. 2026 (owner, round 2): every Ammo find (searches, the zones' tables, enemies) is this share (-10%). */
  findMult: 0.9,
  /** The most Ammo one run can take. */
  maxBring: 400,
  /**
   * Rounds a firearm in hand turns up on every resource search of a run, besides the zone's own finds, on average
   * (0.5–1.5 of it, times the loot bonus): enough that ranged builds find 110–150% of what they fire in the zones of
   * their level (owner, 7. 10. 2026). Zones whose finds already cover it get none.
   */
  searchRounds: {
    forest: 3,
    gas_station: 4.5,
    downtown: 4.5,
    hospital: 3,
    factory: 3,
    military_base: 0,
    mountains: 3.5,
    wasteland: 4,
    secret_bunker: 0.5,
    underground_lab: 5.5,
    survivor_vault: 1,
  } as Record<ZoneId, number>,
};

/**
 * An Ammo find after `findMult` (7. 10. 2026, -10%): the fraction rounds up by chance, so small finds lose their tenth too.
 * `chance` is the caller's random draw in [0, 1).
 */
export function ammoFound(qty: number, chance: number): number {
  if (!AMMO_RULES.perShot || qty <= 0) return qty;
  const x = qty * AMMO_RULES.findMult;
  const whole = Math.floor(x);
  return whole + (chance < x - whole ? 1 : 0);
}

/** Shots a firearm is expected to fire in an NPC fight of this zone (the rule of one a fight: 1). */
export function shotsPerFight(zone: ZoneId): number {
  return AMMO_RULES.perShot ? (AMMO_RULES.shotsPerFight[zone] ?? 5) : 1;
}
